/**
 * How the city view aggregates the stock.
 *
 * Three readings of the same buildings, not three layers: they are mutually
 * exclusive, so this is one mode rather than a set of checkboxes. Overlays that
 * *can* coexist with any of them — the street network, the supply-temperature
 * colouring — stay in the layers menu.
 *
 * Only València has boundaries below the municipality (DATOS §D), so everywhere
 * else the map is Plano and the switch is not offered at all.
 */

/**
 * Coarse to fine, which is the order the reader moves through them: the whole
 * stock, then the 19 districts, then the 88 barrios. Both the top-bar switch and
 * the sidebar tabs are rendered from this list, so the two can never disagree
 * about the order.
 */
export const VISTAS = ["plano", "distritos", "barrios"] as const;

export type Vista = (typeof VISTAS)[number];

export const VISTA_LABELS: Record<Vista, string> = {
  plano: "Plano",
  barrios: "Barrios",
  distritos: "Distritos",
};

export const VISTA_HINTS: Record<Vista, string> = {
  plano: "Sólo los edificios, sin agregación territorial",
  barrios: "Los 88 barrios oficiales, coloreados por la métrica seleccionada",
  distritos: "Los 19 distritos oficiales, coloreados por la métrica seleccionada",
};

/** A view that aggregates — the two that have areas, a panel and a ranking. */
export type AggregateVista = Exclude<Vista, "plano">;

/**
 * True for a view that paints an aggregation over the buildings.
 *
 * A type predicate, not a plain boolean: it is what lets the sidebar branch
 * hand `AggregateVista` to the panel, so a component that reads AREA_NOUNS
 * cannot be rendered in a view that has no areas to name.
 */
export function isAggregate(vista: Vista): vista is AggregateVista {
  return vista !== "plano";
}

/**
 * What to call one of these areas, in the panel's own prose.
 *
 * The sidebar is the same component for both layers, and saying "distrito" over
 * a map of barrios is the same mistake the labels made: the reader is told they
 * are looking at something they are not.
 */
export const AREA_NOUNS: Record<AggregateVista, {
  singular: string;
  plural: string;
  /** The back link out of one area's detail. */
  todos: string;
}> = {
  barrios: { singular: "barrio", plural: "barrios", todos: "Todos los barrios" },
  distritos: { singular: "distrito", plural: "distritos", todos: "Todos los distritos" },
};
